/** The nine climate hazards evaluated per property (used to build the `index_<hazard>` / `index_category_<hazard>` data keys). */
export const CLIMATE_RISK_HAZARDS = [
    "lightning",
    "earthquake",
    "heat",
    "snow",
    "rain",
    "hail",
    "forest fire",
    "winterstorm",
    "flood",
] as const;

export type ClimateRiskHazard = (typeof CLIMATE_RISK_HAZARDS)[number];

/** The seven climate-risk index categories (0-100 raw value buckets), each with its display color and i18n key. */
export const CLIMATE_RISK_INDEX_CLASSES = [
    {color: "#2b83ba", lower: 0, upper: 0, key: "index_none"},
    {color: "#54a0b3", lower: 1, upper: 17, key: "index_very_low"},
    {color: "#acdda4", lower: 18, upper: 34, key: "index_low"},
    {color: "#e6f5b7", lower: 35, upper: 51, key: "index_medium"},
    {color: "#fee09b", lower: 52, upper: 68, key: "index_highish"},
    {color: "#fdaf62", lower: 69, upper: 85, key: "index_high"},
    {color: "#e24330", lower: 86, upper: 100, key: "index_very_high"},
] as const;

export function getColorForClimateRiskIndex(indexCategory: number | null | undefined): string | undefined {
    return indexCategory != null ? CLIMATE_RISK_INDEX_CLASSES[indexCategory]?.color : undefined;
}

export function getClimateRiskTranslationKey(indexCategory: number | null | undefined): string | null {
    return indexCategory != null ? (CLIMATE_RISK_INDEX_CLASSES[indexCategory]?.key ?? null) : null;
}

/** Resolve which of the 7 index categories a raw climate-risk value (0-100) falls into. */
export function getClimateRiskCategoryForValue(value: number | null | undefined): number | null {
    if (value == null) return null;
    const categoryNumber = CLIMATE_RISK_INDEX_CLASSES.findIndex(({lower, upper}) => lower <= value && value <= upper);
    return categoryNumber === -1 ? null : categoryNumber;
}

function hazardKeySuffix(hazard: ClimateRiskHazard): string {
    return hazard.toLowerCase().replace(" ", "_");
}

export function climateRiskIndexKey(hazard: ClimateRiskHazard): string {
    return `index_${hazardKeySuffix(hazard)}`;
}

export function climateRiskIndexCategoryKey(hazard: ClimateRiskHazard): string {
    return `index_category_${hazardKeySuffix(hazard)}`;
}
